import { computed, onBeforeUnmount, ref } from "vue";
import { storeToRefs } from "pinia";
import { useIntersectionObserver } from "@vueuse/core";
import { logEvent } from "~/utils/firebase";
import { useI18n } from "~/composables/useI18n";
import { useUserStore } from "~/stores/user";
import { mapTrackingLanguage } from "~/composables/useSiteComponentTracking";
import { stripLocalePrefix } from "~/utils/i18n-locale-routes";

const SESSION_KEY = "seer_media_square_session_id";

export const MEDIA_PAGE_NAMES = {
	HOME: "media_square_home",
	PODCAST: "media_square_podcast",
	SHORTS: "media_square_shorts",
	ARTICLE: "media_square_article",
};

export const MEDIA_TYPES = {
	PODCAST: "podcast",
	SHORT: "short",
	ARTICLE: "article",
	BANNER: "banner",
};

export function resolveMediaPageName(path) {
	const normalized = stripLocalePrefix(path || "");
	if (normalized.includes("/square/podcast")) return MEDIA_PAGE_NAMES.PODCAST;
	if (normalized.includes("/square/shorts")) return MEDIA_PAGE_NAMES.SHORTS;
	if (normalized.includes("/square/article")) return MEDIA_PAGE_NAMES.ARTICLE;
	return MEDIA_PAGE_NAMES.HOME;
}

function cleanParams(params) {
	return Object.fromEntries(
		Object.entries(params).filter(
			([, value]) => value !== undefined && value !== null && value !== ""
		)
	);
}

function getSessionId() {
	if (!import.meta.client) return "";

	try {
		const cached = sessionStorage.getItem(SESSION_KEY);
		if (cached) return cached;

		const id =
			typeof crypto !== "undefined" && crypto.randomUUID
				? crypto.randomUUID()
				: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
		sessionStorage.setItem(SESSION_KEY, id);
		return id;
	} catch {
		return "";
	}
}

function getEntrySource() {
	if (!import.meta.client) return "direct";
	const referrer = document.referrer;
	if (!referrer) return "direct";

	try {
		const referrerUrl = new URL(referrer);
		if (referrerUrl.host === window.location.host) return "internal";
		return referrerUrl.host;
	} catch {
		return "unknown";
	}
}

export function truncateContentTitle(title, max = 100) {
	if (!title) return "";
	const text = String(title);
	return text.length > max ? `${text.slice(0, max)}...` : text;
}

export function getAuthorName(item, fallback = "") {
	return item?.nickname || item?.author_name || item?.author || fallback;
}

export function getContentDuration(item, formatTime) {
	if (item?.media_second_str) return item.media_second_str;
	if (item?.media_second != null && typeof formatTime === "function") {
		return formatTime(item.media_second);
	}
	return undefined;
}

export function buildContentParams(item, { position, formatTime, authorFallback } = {}) {
	return cleanParams({
		content_id: item?.id != null ? String(item.id) : undefined,
		content_title: truncateContentTitle(item?.title),
		author_name: getAuthorName(item, authorFallback),
		position: position != null ? Number(position) + 1 : undefined,
		duration: getContentDuration(item, formatTime),
	});
}

export function useMediaSquareTracking({ deviceType, pageName }) {
	const { locale } = useI18n();
	const { isLogin } = storeToRefs(useUserStore());

	const baseParams = computed(() =>
		cleanParams({
			session_id: getSessionId(),
			login_status: isLogin.value ? "logged_in" : "guest",
			language: mapTrackingLanguage(locale?.value || "zh-TW"),
			device_type: deviceType,
			page_name: pageName,
			entry_source: getEntrySource(),
		})
	);

	function track(eventName, params = {}) {
		logEvent(eventName, cleanParams({ ...baseParams.value, ...params }));
	}

	function trackMediaPageView(customPageName) {
		track("media_page_view", cleanParams({ page_name: customPageName || pageName }));
	}

	function trackMediaSearchSubmit({ keyword, resultCount }) {
		const value = String(keyword || "").trim();
		track(
			"media_search_submit",
			cleanParams({
				keyword: value,
				keyword_length: value.length,
				result_count: resultCount,
			})
		);
	}

	function trackCreatorCenterClick(entrySource) {
		track(
			"creator_center_click",
			cleanParams({
				creator_status: "unknown",
				entry_source: entrySource,
			})
		);
	}

	function trackMediaMoreClick({ sectionId, mediaType, currentCount }) {
		track(
			"media_more_click",
			cleanParams({
				section_id: sectionId,
				media_type: mediaType,
				current_count: currentCount,
			})
		);
	}

	function trackPodcastCardClick(item, position, formatTime, authorFallback) {
		track("podcast_card_click", buildContentParams(item, { position, formatTime, authorFallback }));
	}

	function trackShortCardClick(item, position, authorFallback) {
		track("short_card_click", buildContentParams(item, { position, authorFallback }));
	}

	function trackArticleCardClick(item, position, formatTime, authorFallback) {
		track("article_card_click", buildContentParams(item, { position, formatTime, authorFallback }));
	}

	function trackVideoPlayClick(item, { mediaType, position, formatTime, authorFallback }) {
		track(
			"video_play_click",
			cleanParams({
				...buildContentParams(item, { position, formatTime, authorFallback }),
				media_type: mediaType,
			})
		);
	}

	function trackBannerImpression({ bannerId = "media_square_promo", position = 1 } = {}) {
		track("media_banner_impression", { banner_id: bannerId, position });
	}

	function trackBannerClick({ bannerId = "media_square_promo", targetUrl = "" } = {}) {
		track("media_banner_click", cleanParams({ banner_id: bannerId, target_url: targetUrl }));
	}

	return {
		track,
		baseParams,
		trackMediaPageView,
		trackMediaSearchSubmit,
		trackCreatorCenterClick,
		trackMediaMoreClick,
		trackPodcastCardClick,
		trackShortCardClick,
		trackArticleCardClick,
		trackVideoPlayClick,
		trackBannerImpression,
		trackBannerClick,
	};
}

/**
 * 媒體卡片曝光：進入視窗 50% 且停留 1 秒後觸發，同一 content_id 去重。
 */
export function useMediaCardImpression({ track, impressionEvent, getItemParams }) {
	const impressedIds = new Set();
	const observerStops = new Map();

	onBeforeUnmount(() => {
		observerStops.forEach(stop => stop());
		observerStops.clear();
	});

	function bindCardRef(el, item, index) {
		const contentId = item?.id;
		if (!contentId) return;

		const impressionKey = String(contentId);

		if (!el) {
			observerStops.get(impressionKey)?.();
			observerStops.delete(impressionKey);
			return;
		}

		if (impressedIds.has(impressionKey) || observerStops.has(impressionKey)) return;

		const targetRef = ref(el);
		let dwellTimer = null;

		const clearDwellTimer = () => {
			if (dwellTimer) {
				clearTimeout(dwellTimer);
				dwellTimer = null;
			}
		};

		const { stop } = useIntersectionObserver(
			targetRef,
			([entry]) => {
				if (impressedIds.has(impressionKey)) return;

				if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
					clearDwellTimer();
					dwellTimer = setTimeout(() => {
						if (impressedIds.has(impressionKey)) return;
						impressedIds.add(impressionKey);
						const extra = typeof getItemParams === "function" ? getItemParams(item, index) : {};
						track(impressionEvent, cleanParams(extra));
						stop();
						observerStops.delete(impressionKey);
					}, 1000);
					return;
				}

				clearDwellTimer();
			},
			{ threshold: [0, 0.5, 1] }
		);

		observerStops.set(impressionKey, () => {
			clearDwellTimer();
			stop();
		});
	}

	return { bindCardRef };
}

/**
 * Banner 曝光：進入視窗 50% 且停留 1 秒後觸發一次。
 */
export function useMediaBannerImpression({ track, bannerId = "media_square_promo", position = 1 }) {
	const tracked = ref(false);
	let dwellTimer = null;

	const clearDwellTimer = () => {
		if (dwellTimer) {
			clearTimeout(dwellTimer);
			dwellTimer = null;
		}
	};

	function bindBannerRef(el) {
		if (!el || tracked.value) return;

		const targetRef = ref(el);
		const { stop } = useIntersectionObserver(
			targetRef,
			([entry]) => {
				if (tracked.value) return;

				if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
					clearDwellTimer();
					dwellTimer = setTimeout(() => {
						if (tracked.value) return;
						tracked.value = true;
						track("media_banner_impression", { banner_id: bannerId, position });
						stop();
					}, 1000);
					return;
				}

				clearDwellTimer();
			},
			{ threshold: [0, 0.5, 1] }
		);

		onBeforeUnmount(() => {
			clearDwellTimer();
			stop();
		});
	}

	return { bindBannerRef, tracked };
}
